import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Copy, Check, Download, Image, Brain, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Math as MathFormula } from "@/components/Math";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";
import { toPng, toJpeg } from "html-to-image";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const commonEquations = [
  { name: "Quadratic Formula", latex: "x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}" },
  { name: "Pythagorean Theorem", latex: "a^2 + b^2 = c^2" },
  { name: "Newton's Second Law", latex: "F = ma" },
  { name: "Einstein's Mass-Energy", latex: "E = mc^2" },
  { name: "Ideal Gas Law", latex: "PV = nRT" },
  { name: "Kinetic Energy", latex: "KE = \\frac{1}{2}mv^2" },
  { name: "Wave Equation", latex: "v = f\\lambda" },
  { name: "Ohm's Law", latex: "V = IR" },
];

export default function LaTeXFormatter() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Tools", url: "https://calclab.app/tools" },
      { name: "LaTeX Formatter", url: "https://calclab.app/calculator/latex-formatter" }
    ], lang),
    (lang) => calculatorToolSchema(
      "LaTeX Equation Formatter",
      "Format and preview mathematical equations in LaTeX. Free online tool with common equation templates.",
      "https://calclab.app/calculator/latex-formatter",
      lang
    )
  ]);

  const [latex, setLatex] = useState("");
  const [copied, setCopied] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(latex);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const insertEquation = (equation: string) => {
    setLatex(equation);
  };

  const exportAsImage = async (format: 'png' | 'jpeg') => {
    if (!previewRef.current) return;
    
    setIsExporting(true);
    try {
      const exportFn = format === 'png' ? toPng : toJpeg;
      const options = format === 'png' 
        ? { pixelRatio: 2 }
        : { 
            backgroundColor: '#ffffff', 
            pixelRatio: 2, 
            quality: 0.95,
            style: { color: '#000000' } // Force black text for JPEG
          };
      
      const dataUrl = await exportFn(previewRef.current, options);
      
      const link = document.createElement('a');
      link.download = `latex-equation.${format}`;
      link.href = dataUrl;
      link.click();
      
      toast.success(`Exported as ${format.toUpperCase()}${format === 'png' ? ' (transparent)' : ''}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export image');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <PageLayout calculatorName="LaTeX Equation Formatter" calculatorPath="/calculator/latex-formatter">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">LaTeX Equation Formatter</h1>
          <p className="text-muted-foreground">
            Format mathematical equations in LaTeX
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>LaTeX Equation Editor</CardTitle>
            <CardDescription>
              Write or select common equations in LaTeX format
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="latex">LaTeX Code</Label>
              <Textarea
                id="latex"
                placeholder="Enter LaTeX equation (e.g., E = mc^2)"
                value={latex}
                onChange={(e) => setLatex(e.target.value)}
                rows={4}
                className="font-mono"
              />
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyToClipboard}
                  disabled={!latex}
                >
                  {copied ? (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2 h-4 w-4" />
                      Copy LaTeX
                    </>
                  )}
                </Button>
              </div>
            </div>

            {latex && (
              <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-xl">Preview</CardTitle>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" disabled={isExporting}>
                        {isExporting ? (
                          "Exporting..."
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Export Image
                          </>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => exportAsImage('png')}>
                        <Image className="h-4 w-4 mr-2" />
                        PNG (Transparent)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportAsImage('jpeg')}>
                        <Image className="h-4 w-4 mr-2" />
                        JPEG (White BG)
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardHeader>
                <CardContent>
                  <div ref={previewRef} className="p-6 rounded-lg bg-slate-100 dark:bg-slate-200 [&_.katex]:!text-black">
                    <MathFormula block>{latex}</MathFormula>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Rendered LaTeX equation • PNG is transparent, JPEG has white background
                  </p>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Common Equations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-2">
                  {commonEquations.map((eq) => (
                    <Button
                      key={eq.name}
                      variant="outline"
                      className="justify-start h-auto py-3"
                      onClick={() => insertEquation(eq.latex)}
                    >
                      <div className="text-left w-full flex items-center gap-4">
                        <div className="flex-1">
                          <div className="font-medium text-sm mb-1">{eq.name}</div>
                          <div className="font-mono text-xs text-muted-foreground">
                            {eq.latex}
                          </div>
                        </div>
                        <div className="text-right">
                          <MathFormula>{eq.latex}</MathFormula>
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">LaTeX Syntax Guide</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• Fractions: <code className="text-foreground">\frac{"{numerator}"}{"{denominator}"}</code></p>
                <p>• Square root: <code className="text-foreground">\sqrt{"{x}"}</code></p>
                <p>• Superscript: <code className="text-foreground">x^2</code> or <code className="text-foreground">x^{"{2n}"}</code></p>
                <p>• Subscript: <code className="text-foreground">x_1</code> or <code className="text-foreground">x_{"{ab}"}</code></p>
                <p>• Greek letters: <code className="text-foreground">\alpha \beta \gamma \delta \pi</code></p>
                <p>• Summation: <code className="text-foreground">\sum_{"{i=1}"}^{"{n}"}</code></p>
                <p>• Integral: <code className="text-foreground">\int_{"{a}"}^{"{b}"}</code></p>
              </CardContent>
            </Card>

            {/* LaTeX Learning Center CTA */}
            <Card className="border-primary/20 bg-primary/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Master LaTeX Formatting
                </CardTitle>
                <CardDescription>
                  Learn to write professional equations and edit SuperCalc outputs with our interactive tutorials.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" asChild className="w-full sm:w-auto">
                  <Link to="/learn/latex">
                    Explore LaTeX Learning Center
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* SuperCalc CTA */}
            <Card className="bg-gradient-to-br from-supercalc-brand/10 to-supercalc-brand/5 border-supercalc-brand/30 overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-r from-supercalc-brand/5 via-transparent to-supercalc-brand/5 animate-pulse" />
              <CardHeader className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <Brain className="h-6 w-6 text-supercalc-brand" />
                  <Sparkles className="h-4 w-4 text-supercalc-brand animate-pulse" />
                </div>
                <CardTitle className="text-xl text-supercalc-brand">
                  Need Full LaTeX Reports?
                </CardTitle>
                <CardDescription>
                  SuperCalc AI can generate complete LaTeX-formatted reports, lesson plans, and step-by-step solutions from plain text questions.
                </CardDescription>
              </CardHeader>
              <CardContent className="relative space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-background/80 rounded-lg border">
                    <p className="font-medium text-muted-foreground mb-1">Example Input:</p>
                    <p className="text-foreground">"Solve x² + 5x + 6 = 0 with full steps"</p>
                  </div>
                  <div className="p-3 bg-background/80 rounded-lg border">
                    <p className="font-medium text-muted-foreground mb-1">SuperCalc Output:</p>
                    <div className="text-foreground">
                      <MathFormula>{"x = \\frac{-5 \\pm \\sqrt{25-24}}{2} = -2, -3"}</MathFormula>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">+ full explanation & steps</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild className="flex-1 bg-supercalc-brand hover:bg-supercalc-brand/90">
                    <Link to="/supercalc">
                      <Brain className="h-4 w-4 mr-2" />
                      Try SuperCalc AI
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </Button>
                  <Button variant="outline" asChild className="flex-1">
                    <Link to="/membership">
                      Get Member Access
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
